import { getItemDef } from './items'
import {
  isDenBoschFullyExplored,
  isLocationUnlocked,
  LOCATION_LIST,
} from './locations'
import { isSkillUnlocked } from './skills'
import type {
  HideoutTierId,
  ItemStack,
  LocationId,
  LocationExplorationState,
  QuestRuntimeState,
  QuestId,
  SkillKey,
  Survivor,
  SurvivorRole,
  WorkstationId,
  WorkstationRuntimeState,
} from './types'

/** Extendable quest ids */
export type { QuestId } from './types'

export const STARTER_SURVIVAL_QUEST_ID = 'starter_survival' as const
export const DEN_BOSCH_GATEWAY_QUEST_ID = 'den_bosch_gateway' as const satisfies QuestId
export const VEHICLE_QUEST_ID = 'vehicle_quest' as const satisfies QuestId
export const COOKING_SKILL_QUEST_ID = 'cooking_skill_quest' as const satisfies QuestId
export const HIDEOUT_MOVE_QUEST_ID = 'hideout_move_quest' as const satisfies QuestId
export const UTRECHT_GATEWAY_QUEST_ID = 'utrecht_gateway' as const satisfies QuestId

/** Number of phases in the vehicle intro quest */
export const VEHICLE_QUEST_PHASE_COUNT = 2

/** Number of phases in the cooking skill quest */
export const COOKING_SKILL_QUEST_PHASE_COUNT = 3

/** Ordered phases for the tutorial-adjacent starter quest */
export const STARTER_SURVIVAL_PHASE_COUNT = 4

/** Number of phases in the hideout move quest */
export const HIDEOUT_MOVE_QUEST_PHASE_COUNT = 3

/** i18n keys for quest log checklist rows (must stay aligned with `STARTER_SURVIVAL_PHASE_COUNT`) */
export const STARTER_QUEST_PHASE_MESSAGE_KEYS = [
  'quest_phase_send_bridge',
  'quest_phase_return_loot',
  'quest_phase_build_campfire',
  'quest_phase_feed_hunger',
] as const

/** i18n keys for vehicle intro checklist (aligned with `VEHICLE_QUEST_PHASE_COUNT`) */
export const VEHICLE_QUEST_PHASE_MESSAGE_KEYS = [
  'quest_phase_build_garage',
  'quest_phase_farm_expedition',
] as const

/** i18n keys for cooking skill quest checklist (aligned with `COOKING_SKILL_QUEST_PHASE_COUNT`) */
export const COOKING_SKILL_QUEST_PHASE_MESSAGE_KEYS = [
  'quest_cooking_phase_find_book',
  'quest_cooking_phase_learn_skill',
  'quest_cooking_phase_assign_kitchen',
] as const

/** i18n keys for hideout move quest checklist (aligned with `HIDEOUT_MOVE_QUEST_PHASE_COUNT`) */
export const HIDEOUT_MOVE_QUEST_PHASE_MESSAGE_KEYS = [
  'quest_hideout_move_phase_farm_trip',
  'quest_hideout_move_phase_relocate',
  'quest_hideout_move_phase_upgrade_base',
] as const

/** i18n keys for gateway objective labels shown with progress bars */
export const DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS = [
  'quest_den_bosch_obj_farm_label',
  'quest_den_bosch_obj_food_label',
  'quest_den_bosch_obj_water_label',
] as const

/** Display order in the Quest log UI */
export const QUEST_LOG_ORDER: readonly QuestId[] = [
  STARTER_SURVIVAL_QUEST_ID,
  COOKING_SKILL_QUEST_ID,
  VEHICLE_QUEST_ID,
  DEN_BOSCH_GATEWAY_QUEST_ID,
  HIDEOUT_MOVE_QUEST_ID,
  UTRECHT_GATEWAY_QUEST_ID,
]

export const DEN_BOSCH_GATEWAY_TARGETS = {
  abandonedFarmTrips: 1,
  /** Requires several solid hauls — luxury items alone cannot rush both caps */
  foodLootValue: 14,
  waterLootValue: 14,
} as const

/** Numeric caps aligned with `DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS` */
export const DEN_BOSCH_GATEWAY_TARGET_VALUES: readonly number[] = [
  DEN_BOSCH_GATEWAY_TARGETS.abandonedFarmTrips,
  DEN_BOSCH_GATEWAY_TARGETS.foodLootValue,
  DEN_BOSCH_GATEWAY_TARGETS.waterLootValue,
]

/** Post–broadcast station stockpile objectives (scrap value excludes electronics stacks counted separately) */
export const UTRECHT_GATEWAY_TARGETS = {
  scrapLootValuePostBroadcast: 26,
  electronicsLootValuePostBroadcast: 14,
  nonTraderRecruits: 2,
} as const

/** Shown only on phase 3 (parallel progress rows) */
export const UTRECHT_GATEWAY_POST_BROADCAST_LABEL_KEYS = [
  'quest_utrecht_obj_recruit_label',
  'quest_utrecht_obj_scrap_label',
  'quest_utrecht_obj_electronics_label',
] as const

export const UTRECHT_GATEWAY_POST_BROADCAST_TARGET_VALUES: readonly number[] = [
  UTRECHT_GATEWAY_TARGETS.nonTraderRecruits,
  UTRECHT_GATEWAY_TARGETS.scrapLootValuePostBroadcast,
  UTRECHT_GATEWAY_TARGETS.electronicsLootValuePostBroadcast,
]

/** Main journal checklist (aligned with phaseIndex 0–3) */
export const UTRECHT_GATEWAY_PHASE_COUNT = 4

export const UTRECHT_GATEWAY_PHASE_MESSAGE_KEYS = [
  'quest_utrecht_phase_find_radio',
  'quest_utrecht_phase_repair_radio',
  'quest_utrecht_phase_build_broadcast',
  'quest_utrecht_phase_north_supply',
] as const

export const UTRECHT_GATEWAY_PHASE_DETAIL_KEYS = [
  'quest_utrecht_phase_find_radio_detail',
  'quest_utrecht_phase_repair_radio_detail',
  'quest_utrecht_phase_build_broadcast_detail',
  'quest_utrecht_phase_north_supply_detail',
] as const

export type QuestLogUiCategory =
  | 'survival'
  | 'vehicle'
  | 'gateway'
  | 'cooking'
  | 'hideout'
  | 'utrecht'

export type QuestLogCollapsedHint =
  | { kind: 'message'; messageKey: string }
  | { kind: 'objectives'; done: number; total: number }

export type QuestLogBody =
  | { kind: 'starter'; state: 'inactive' }
  | { kind: 'starter'; state: 'active'; phaseIndex: number }
  | {
      kind: 'starter'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }
  | { kind: 'vehicle'; state: 'active'; phaseIndex: number }
  | {
      kind: 'vehicle'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }
  | { kind: 'gateway'; state: 'available'; startHintKey: string }
  | {
      kind: 'gateway'
      state: 'active'
      abandonedFarmTripsCompleted: number
      foodLootValueAccumulated: number
      waterLootValueAccumulated: number
    }
  | {
      kind: 'gateway'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }
  | { kind: 'cooking'; state: 'active'; phaseIndex: number }
  | {
      kind: 'cooking'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }
  | { kind: 'hideout_move'; state: 'active'; phaseIndex: number }
  | {
      kind: 'hideout_move'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }
  | { kind: 'utrecht_gateway'; state: 'available'; startHintKey: string }
  | {
      kind: 'utrecht_gateway'
      state: 'active'
      phaseIndex: number
      broadcastBuilt: boolean
      nonTraderRecruitsSinceStart: number
      scrapLootValuePostBroadcast: number
      electronicsLootValuePostBroadcast: number
    }
  | {
      kind: 'utrecht_gateway'
      state: 'completed'
      completeTitleKey: string
      completeSubtitleKey: string
      rewardClaimed: boolean
    }

export type QuestLogEntry = {
  id: QuestId
  category: QuestLogUiCategory
  badgeKey: string
  titleKey: string
  bodyKey: string
  defaultExpanded: boolean
  collapsedHint: QuestLogCollapsedHint
  body: QuestLogBody
}

function gatewayObjectivesDoneCount(q: QuestRuntimeState['denBoschGateway']): number {
  if (q.status !== 'active') return 0
  return [
    q.abandonedFarmTripsCompleted >= DEN_BOSCH_GATEWAY_TARGETS.abandonedFarmTrips,
    q.foodLootValueAccumulated >= DEN_BOSCH_GATEWAY_TARGETS.foodLootValue,
    q.waterLootValueAccumulated >= DEN_BOSCH_GATEWAY_TARGETS.waterLootValue,
  ].filter(Boolean).length
}

function buildStarterQuestLogEntry(q: QuestRuntimeState): QuestLogEntry {
  const s = q.starterSurvival

  const doneCount =
    s.status === 'completed'
      ? STARTER_SURVIVAL_PHASE_COUNT
      : s.status === 'active'
        ? s.phaseIndex
        : 0

  let collapsedHint: QuestLogCollapsedHint
  if (s.status === 'completed') {
    collapsedHint = { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
  } else if (s.status === 'inactive') {
    collapsedHint = { kind: 'message', messageKey: 'quest_starter_inactive' }
  } else {
    collapsedHint = {
      kind: 'objectives',
      done: doneCount,
      total: STARTER_SURVIVAL_PHASE_COUNT,
    }
  }

  let body: QuestLogBody
  if (s.status === 'inactive') {
    body = { kind: 'starter', state: 'inactive' }
  } else if (s.status === 'completed') {
    body = {
      kind: 'starter',
      state: 'completed',
      completeTitleKey: 'quest_starter_complete',
      completeSubtitleKey: 'quest_starter_reward',
      rewardClaimed: s.acknowledged,
    }
  } else {
    body = { kind: 'starter', state: 'active', phaseIndex: s.phaseIndex }
  }

  return {
    id: STARTER_SURVIVAL_QUEST_ID,
    category: 'survival',
    badgeKey: 'quests_starter_tag',
    titleKey: 'quest_starter_survival_title',
    bodyKey: 'quest_starter_survival_body',
    defaultExpanded: s.status === 'active',
    collapsedHint,
    body,
  }
}

function buildVehicleQuestLogEntry(q: QuestRuntimeState): QuestLogEntry | null {
  const v = q.vehicleQuest
  if (v.status === 'inactive') return null

  const doneCount =
    v.status === 'completed'
      ? VEHICLE_QUEST_PHASE_COUNT
      : v.status === 'active'
        ? v.phaseIndex
        : 0

  const collapsedHint: QuestLogCollapsedHint =
    v.status === 'completed'
      ? { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
      : {
          kind: 'objectives',
          done: doneCount,
          total: VEHICLE_QUEST_PHASE_COUNT,
        }

  const body: QuestLogBody =
    v.status === 'completed'
      ? {
          kind: 'vehicle',
          state: 'completed',
          completeTitleKey: 'quest_vehicle_complete',
          completeSubtitleKey: 'quest_vehicle_reward',
          rewardClaimed: v.acknowledged,
        }
      : { kind: 'vehicle', state: 'active', phaseIndex: v.phaseIndex }

  return {
    id: VEHICLE_QUEST_ID,
    category: 'vehicle',
    badgeKey: 'quests_vehicle_tag',
    titleKey: 'quest_vehicle_title',
    bodyKey: 'quest_vehicle_body',
    defaultExpanded: v.status === 'active',
    collapsedHint,
    body,
  }
}

function buildCookingSkillQuestLogEntry(q: QuestRuntimeState): QuestLogEntry | null {
  const c = q.cookingSkillQuest
  if (c.status === 'inactive') return null

  const doneCount =
    c.status === 'completed'
      ? COOKING_SKILL_QUEST_PHASE_COUNT
      : c.status === 'active'
        ? c.phaseIndex
        : 0

  const collapsedHint: QuestLogCollapsedHint =
    c.status === 'completed'
      ? { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
      : { kind: 'objectives', done: doneCount, total: COOKING_SKILL_QUEST_PHASE_COUNT }

  const body: QuestLogBody =
    c.status === 'completed'
      ? {
          kind: 'cooking',
          state: 'completed',
          completeTitleKey: 'quest_cooking_complete',
          completeSubtitleKey: 'quest_cooking_reward',
          rewardClaimed: c.acknowledged,
        }
      : { kind: 'cooking', state: 'active', phaseIndex: c.phaseIndex }

  return {
    id: COOKING_SKILL_QUEST_ID,
    category: 'cooking',
    badgeKey: 'quest_cooking_skill_tag',
    titleKey: 'quest_cooking_skill_title',
    bodyKey: 'quest_cooking_skill_body',
    defaultExpanded: c.status === 'active',
    collapsedHint,
    body,
  }
}

function buildDenBoschGatewayQuestLogEntry(q: QuestRuntimeState): QuestLogEntry | null {
  const g = q.denBoschGateway
  if (g.status === 'inactive') return null

  let collapsedHint: QuestLogCollapsedHint
  if (g.status === 'completed') {
    collapsedHint = { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
  } else if (g.status === 'available') {
    collapsedHint = { kind: 'message', messageKey: 'quest_start_button' }
  } else {
    collapsedHint = {
      kind: 'objectives',
      done: gatewayObjectivesDoneCount(g),
      total: DEN_BOSCH_GATEWAY_OBJECTIVE_LABEL_KEYS.length,
    }
  }

  let body: QuestLogBody
  if (g.status === 'available') {
    body = {
      kind: 'gateway',
      state: 'available',
      startHintKey: 'quest_den_bosch_gateway_start_hint',
    }
  } else if (g.status === 'completed') {
    body = {
      kind: 'gateway',
      state: 'completed',
      completeTitleKey: 'quest_den_bosch_gateway_complete',
      completeSubtitleKey: 'quest_den_bosch_gateway_reward',
      rewardClaimed: g.acknowledged,
    }
  } else {
    body = {
      kind: 'gateway',
      state: 'active',
      abandonedFarmTripsCompleted: g.abandonedFarmTripsCompleted,
      foodLootValueAccumulated: g.foodLootValueAccumulated,
      waterLootValueAccumulated: g.waterLootValueAccumulated,
    }
  }

  return {
    id: DEN_BOSCH_GATEWAY_QUEST_ID,
    category: 'gateway',
    badgeKey: 'quests_den_bosch_tag',
    titleKey: 'quest_den_bosch_gateway_title',
    bodyKey: 'quest_den_bosch_gateway_body',
    defaultExpanded: g.status === 'available' || g.status === 'active',
    collapsedHint,
    body,
  }
}

function buildHideoutMoveQuestLogEntry(q: QuestRuntimeState): QuestLogEntry | null {
  const h = q.hideoutMoveQuest
  if (h.status === 'inactive') return null

  const doneCount =
    h.status === 'completed'
      ? HIDEOUT_MOVE_QUEST_PHASE_COUNT
      : h.status === 'active'
        ? h.phaseIndex
        : 0

  const collapsedHint: QuestLogCollapsedHint =
    h.status === 'completed'
      ? { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
      : { kind: 'objectives', done: doneCount, total: HIDEOUT_MOVE_QUEST_PHASE_COUNT }

  const body: QuestLogBody =
    h.status === 'completed'
      ? {
          kind: 'hideout_move',
          state: 'completed',
          completeTitleKey: 'quest_hideout_move_complete',
          completeSubtitleKey: 'quest_hideout_move_reward',
          rewardClaimed: h.acknowledged,
        }
      : { kind: 'hideout_move', state: 'active', phaseIndex: h.phaseIndex }

  return {
    id: HIDEOUT_MOVE_QUEST_ID,
    category: 'hideout',
    badgeKey: 'quest_hideout_move_tag',
    titleKey: 'quest_hideout_move_title',
    bodyKey: 'quest_hideout_move_body',
    defaultExpanded: h.status === 'active',
    collapsedHint,
    body,
  }
}

function stashQty(inventory: ItemStack[], defId: string): number {
  return inventory.reduce((sum, s) => sum + (s.defId === defId ? s.quantity : 0), 0)
}

/**
 * Advances Utrecht gateway `phaseIndex` from inventory/workstation truth (never regresses).
 * Call after loot, crafts, builds, and on tick.
 */
export function syncUtrechtGatewayPhaseFromState(
  prev: QuestRuntimeState,
  inventory: ItemStack[],
  workstations: Record<string, WorkstationRuntimeState | undefined>,
): QuestRuntimeState | null {
  const g = prev.utrechtGateway
  if (g.status !== 'active') return null

  const broken = stashQty(inventory, 'scrap-radio-broken')
  const radio = stashQty(inventory, 'scrap-radio')
  const broad = workstations.broadcast_station?.built === true

  const cur =
    typeof (g as { phaseIndex?: number }).phaseIndex === 'number'
      ? (g as { phaseIndex: number }).phaseIndex
      : 0
  let next = Math.max(0, Math.min(UTRECHT_GATEWAY_PHASE_COUNT - 1, cur))

  if (broad) next = Math.max(next, 3)
  if (radio > 0) next = Math.max(next, 2)
  else if (broken > 0) next = Math.max(next, 1)

  if (next === cur) return null
  return {
    ...prev,
    utrechtGateway: {
      ...g,
      phaseIndex: next,
    },
  }
}

function buildUtrechtGatewayQuestLogEntry(
  q: QuestRuntimeState,
  broadcastBuilt: boolean,
): QuestLogEntry | null {
  const u = q.utrechtGateway
  if (u.status === 'inactive') return null

  const phaseIdx =
    u.status === 'active'
      ? typeof u.phaseIndex === 'number'
        ? u.phaseIndex
        : 0
      : 0

  let collapsedHint: QuestLogCollapsedHint
  if (u.status === 'completed') {
    collapsedHint = { kind: 'message', messageKey: 'quests_collapsed_complete_short' }
  } else if (u.status === 'available') {
    collapsedHint = { kind: 'message', messageKey: 'quest_start_button' }
  } else {
    collapsedHint = {
      kind: 'objectives',
      done: phaseIdx,
      total: UTRECHT_GATEWAY_PHASE_COUNT,
    }
  }

  let body: QuestLogBody
  if (u.status === 'available') {
    body = {
      kind: 'utrecht_gateway',
      state: 'available',
      startHintKey: 'quest_utrecht_gateway_start_hint',
    }
  } else if (u.status === 'completed') {
    body = {
      kind: 'utrecht_gateway',
      state: 'completed',
      completeTitleKey: 'quest_utrecht_gateway_complete',
      completeSubtitleKey: 'quest_utrecht_gateway_reward',
      rewardClaimed: u.acknowledged,
    }
  } else {
    body = {
      kind: 'utrecht_gateway',
      state: 'active',
      phaseIndex: phaseIdx,
      broadcastBuilt,
      nonTraderRecruitsSinceStart: u.nonTraderRecruitsSinceStart,
      scrapLootValuePostBroadcast: u.scrapLootValuePostBroadcast,
      electronicsLootValuePostBroadcast: u.electronicsLootValuePostBroadcast,
    }
  }

  return {
    id: UTRECHT_GATEWAY_QUEST_ID,
    category: 'utrecht',
    badgeKey: 'quests_utrecht_tag',
    titleKey: 'quest_utrecht_gateway_title',
    bodyKey: 'quest_utrecht_gateway_body',
    defaultExpanded: u.status === 'available' || u.status === 'active',
    collapsedHint,
    body,
  }
}

const QUEST_LOG_BUILDERS: Omit<
  Record<QuestId, (q: QuestRuntimeState) => QuestLogEntry | null>,
  'utrecht_gateway'
> = {
  starter_survival: buildStarterQuestLogEntry,
  vehicle_quest: buildVehicleQuestLogEntry,
  den_bosch_gateway: buildDenBoschGatewayQuestLogEntry,
  cooking_skill_quest: buildCookingSkillQuestLogEntry,
  hideout_move_quest: buildHideoutMoveQuestLogEntry,
}

/** Resolved quest rows for the Quest log (visibility, hints, and body variant). */
export function getQuestLogEntries(
  q: QuestRuntimeState,
  opts?: { broadcastBuilt?: boolean },
): QuestLogEntry[] {
  const out: QuestLogEntry[] = []
  for (const id of QUEST_LOG_ORDER) {
    const built =
      id === UTRECHT_GATEWAY_QUEST_ID
        ? buildUtrechtGatewayQuestLogEntry(q, opts?.broadcastBuilt ?? false)
        : QUEST_LOG_BUILDERS[id](q)
    if (built) out.push(built)
  }
  return out
}

export const DEFAULT_QUEST_STATE: QuestRuntimeState = {
  starterSurvival: { status: 'inactive' },
  denBoschGateway: { status: 'inactive' },
  vehicleQuest: { status: 'inactive' },
  cookingSkillQuest: { status: 'inactive' },
  hideoutMoveQuest: { status: 'inactive' },
  utrechtGateway: { status: 'inactive' },
}

/** Normalize persisted/partial quest blobs from older saves or sparse cloud payloads */
export function mergeQuestRuntimeState(
  raw: Partial<QuestRuntimeState> | undefined | null,
): QuestRuntimeState {
  const ug = raw?.utrechtGateway ?? DEFAULT_QUEST_STATE.utrechtGateway
  const utrechtGateway =
    ug.status === 'active' && typeof (ug as { phaseIndex?: number }).phaseIndex !== 'number'
      ? { ...ug, phaseIndex: 0 }
      : ug

  return {
    starterSurvival: raw?.starterSurvival ?? DEFAULT_QUEST_STATE.starterSurvival,
    denBoschGateway: raw?.denBoschGateway ?? DEFAULT_QUEST_STATE.denBoschGateway,
    vehicleQuest: raw?.vehicleQuest ?? DEFAULT_QUEST_STATE.vehicleQuest,
    cookingSkillQuest: raw?.cookingSkillQuest ?? DEFAULT_QUEST_STATE.cookingSkillQuest,
    hideoutMoveQuest: raw?.hideoutMoveQuest ?? DEFAULT_QUEST_STATE.hideoutMoveQuest,
    utrechtGateway,
  }
}

export type QuestProgressEvent =
  | { type: 'expedition_started'; locationId: LocationId }
  | {
      type: 'expedition_returned'
      /** Expeditions that finished this tick (always > 0 when this event fires) */
      resolvedCount: number
    }
  | { type: 'expedition_returned_from'; locationId: LocationId }
  | { type: 'workstation_built'; stationId: WorkstationId }
  | { type: 'kitchen_feed' }
  | { type: 'book_obtained'; bookDefId: string }
  | { type: 'skill_learned'; skillId: SkillKey }
  | { type: 'kitchen_assigned' }
  | { type: 'hideout_relocated' }
  | { type: 'workstation_upgraded'; stationId: WorkstationId; newLevel: number }

export function initialStarterQuestActive(): QuestRuntimeState['starterSurvival'] {
  return { status: 'active', phaseIndex: 0 }
}

/** While phases 0–1, player should only use the tutorial bridge (matches legacy coach flow). */
export function starterQuestRestrictsExpeditions(q: QuestRuntimeState): boolean {
  const s = q.starterSurvival
  return s.status === 'active' && s.phaseIndex <= 1
}

/** Short first trip only on phase 0 (first send). */
export function starterQuestUsesTutorialDuration(q: QuestRuntimeState): boolean {
  const s = q.starterSurvival
  return s.status === 'active' && s.phaseIndex === 0
}

/** Trips tab: show Den Bosch region header once gateway quest is offered or underway */
export function isDenBoschRegionVisibleInTrips(q: QuestRuntimeState): boolean {
  const s = q.denBoschGateway.status
  return s === 'available' || s === 'active' || s === 'completed'
}

/** Every Rural South destination unlocked via exploration chain */
export function isRuralSouthFullyUnlocked(
  progress: number,
  explorationState: Partial<Record<LocationId, LocationExplorationState>> | undefined,
): boolean {
  return LOCATION_LIST.filter((l) => l.region === 'rural-south').every((l) =>
    isLocationUnlocked(l.id, progress, explorationState),
  )
}

/** inactive → available when Rural South is complete */
export function reduceDenBoschGatewaySyncAvailability(
  prev: QuestRuntimeState,
  progress: number,
  explorationState: Partial<Record<LocationId, LocationExplorationState>> | undefined,
): QuestRuntimeState | null {
  if (prev.denBoschGateway.status !== 'inactive') return null
  if (!isRuralSouthFullyUnlocked(progress, explorationState)) return null
  return {
    ...prev,
    denBoschGateway: { status: 'available' },
  }
}

/** available → active (manual start) */
export function reduceDenBoschGatewayStart(prev: QuestRuntimeState): QuestRuntimeState | null {
  if (prev.denBoschGateway.status !== 'available') return null
  return {
    ...prev,
    denBoschGateway: {
      status: 'active',
      abandonedFarmTripsCompleted: 0,
      foodLootValueAccumulated: 0,
      waterLootValueAccumulated: 0,
    },
  }
}

/**
 * While active: count loot from a completed expedition report toward caps.
 * Objectives only progress after the player taps Start on this quest.
 */
export function reduceDenBoschGatewayExpeditionLoot(
  prev: QuestRuntimeState,
  locationId: LocationId,
  collectedItems: ItemStack[],
): QuestRuntimeState | null {
  const gate = prev.denBoschGateway
  if (gate.status !== 'active') return null

  let trips = gate.abandonedFarmTripsCompleted
  if (locationId === 'abandoned-farm') {
    trips = Math.min(
      DEN_BOSCH_GATEWAY_TARGETS.abandonedFarmTrips,
      trips + 1,
    )
  }

  let food = gate.foodLootValueAccumulated
  let water = gate.waterLootValueAccumulated
  for (const stack of collectedItems) {
    const def = getItemDef(stack.defId)
    if (!def) continue
    const add = def.value * stack.quantity
    if (def.category === 'food') {
      food = Math.min(DEN_BOSCH_GATEWAY_TARGETS.foodLootValue, food + add)
    } else if (def.category === 'water') {
      water = Math.min(DEN_BOSCH_GATEWAY_TARGETS.waterLootValue, water + add)
    }
  }

  const objectivesDone =
    trips >= DEN_BOSCH_GATEWAY_TARGETS.abandonedFarmTrips &&
    food >= DEN_BOSCH_GATEWAY_TARGETS.foodLootValue &&
    water >= DEN_BOSCH_GATEWAY_TARGETS.waterLootValue

  if (objectivesDone) {
    return {
      ...prev,
      denBoschGateway: {
        status: 'completed',
        completedAt: Date.now(),
        acknowledged: false,
      },
    }
  }

  if (
    trips === gate.abandonedFarmTripsCompleted &&
    food === gate.foodLootValueAccumulated &&
    water === gate.waterLootValueAccumulated
  ) {
    return null
  }

  return {
    ...prev,
    denBoschGateway: {
      status: 'active',
      abandonedFarmTripsCompleted: trips,
      foodLootValueAccumulated: food,
      waterLootValueAccumulated: water,
    },
  }
}

/**
 * Pure reducer for starter quest. Returns updated quest state or null if unchanged.
 */
export function reduceStarterQuest(
  prev: QuestRuntimeState,
  event: QuestProgressEvent,
): QuestRuntimeState | null {
  const sq = prev.starterSurvival
  if (sq.status !== 'active') return null

  const idx = sq.phaseIndex
  let nextIdx = idx
  let completed = false

  switch (idx) {
    case 0:
      if (event.type === 'expedition_started') {
        nextIdx = 1
      }
      break
    case 1:
      if (
        event.type === 'expedition_returned' &&
        event.resolvedCount > 0
      ) {
        nextIdx = 2
      }
      break
    case 2:
      if (event.type === 'workstation_built' && event.stationId === 'kitchen') {
        nextIdx = 3
      }
      break
    case 3:
      if (event.type === 'kitchen_feed') {
        completed = true
      }
      break
    default:
      break
  }

  if (completed) {
    return {
      ...prev,
      starterSurvival: {
        status: 'completed',
        completedAt: Date.now(),
        acknowledged: false,
      },
    }
  }

  if (nextIdx !== idx) {
    return {
      ...prev,
      starterSurvival: { status: 'active', phaseIndex: nextIdx },
    }
  }

  return null
}

/**
 * Vehicle quest — activated automatically once den_bosch_gateway is completed.
 * Phase 0: Build the garage (level 1).
 * Phase 1: Send anyone to the Abandoned Farm.
 */
export function reduceVehicleQuestAvailability(
  prev: QuestRuntimeState,
  garageAlreadyBuilt = false,
): QuestRuntimeState | null {
  if (prev.vehicleQuest.status !== 'inactive') return null
  if (prev.denBoschGateway.status !== 'completed') return null
  return {
    ...prev,
    vehicleQuest: { status: 'active', phaseIndex: garageAlreadyBuilt ? 1 : 0 },
  }
}

/**
 * Pure reducer for vehicle quest events.
 * Returns updated state or null if unchanged.
 */
export function reduceVehicleQuest(
  prev: QuestRuntimeState,
  event: QuestProgressEvent,
): QuestRuntimeState | null {
  const vq = prev.vehicleQuest
  if (vq.status !== 'active') return null

  const idx = vq.phaseIndex

  switch (idx) {
    case 0:
      if (event.type === 'workstation_built' && event.stationId === 'garage') {
        return { ...prev, vehicleQuest: { status: 'active', phaseIndex: 1 } }
      }
      break
    case 1:
      if (
        event.type === 'expedition_returned_from' &&
        event.locationId === 'abandoned-farm'
      ) {
        return {
          ...prev,
          vehicleQuest: {
            status: 'completed',
            completedAt: Date.now(),
            acknowledged: false,
          },
        }
      }
      break
    default:
      break
  }

  return null
}

/** True once vehicle quest is completed — enables random vehicle drops across locations */
export function isVehicleQuestComplete(q: QuestRuntimeState): boolean {
  return q.vehicleQuest.status === 'completed'
}

/**
 * Activates the cooking skill quest when the player has at least 3 survivors.
 * Pre-advances the phase if the player already has the book or cooking skill.
 */
export function reduceCookingSkillQuestAvailability(
  prev: QuestRuntimeState,
  survivorCount: number,
  inventory: ItemStack[],
  survivors: Survivor[],
): QuestRuntimeState | null {
  if (prev.cookingSkillQuest.status !== 'inactive') return null
  if (survivorCount < 3) return null

  const hasBook = inventory.some((s) => s.defId === 'book-home-cooking')
  const hasCookingSkill = survivors.some((s) => isSkillUnlocked(s, 'cooking'))

  let phaseIndex = 0
  if (hasCookingSkill) phaseIndex = 2
  else if (hasBook) phaseIndex = 1

  return {
    ...prev,
    cookingSkillQuest: { status: 'active', phaseIndex },
  }
}

/**
 * Pure reducer for cooking skill quest events.
 * Returns updated state or null if unchanged.
 */
export function reduceCookingSkillQuest(
  prev: QuestRuntimeState,
  event: QuestProgressEvent,
): QuestRuntimeState | null {
  const cq = prev.cookingSkillQuest
  if (cq.status !== 'active') return null

  const idx = cq.phaseIndex

  switch (idx) {
    case 0:
      if (event.type === 'book_obtained' && event.bookDefId === 'book-home-cooking') {
        return { ...prev, cookingSkillQuest: { status: 'active', phaseIndex: 1 } }
      }
      break
    case 1:
      if (event.type === 'skill_learned' && event.skillId === 'cooking') {
        return { ...prev, cookingSkillQuest: { status: 'active', phaseIndex: 2 } }
      }
      break
    case 2:
      if (event.type === 'kitchen_assigned') {
        return {
          ...prev,
          cookingSkillQuest: {
            status: 'completed',
            completedAt: Date.now(),
            acknowledged: false,
          },
        }
      }
      break
    default:
      break
  }

  return null
}

/**
 * Activates the hideout move quest when den_bosch_gateway is completed.
 * Pre-advances based on current hideout tier and base station level.
 */
export function reduceHideoutMoveQuestAvailability(
  prev: QuestRuntimeState,
  hideoutTierId: HideoutTierId,
  baseStationLevel: number,
): QuestRuntimeState | null {
  if (prev.hideoutMoveQuest.status !== 'inactive') return null
  if (prev.denBoschGateway.status !== 'completed') return null

  const alreadyRelocated = hideoutTierId !== 'forest_hideout'
  const baseAlreadyLevel2 = baseStationLevel >= 2

  if (alreadyRelocated && baseAlreadyLevel2) {
    return {
      ...prev,
      hideoutMoveQuest: { status: 'completed', completedAt: Date.now(), acknowledged: true },
    }
  }

  const phaseIndex = alreadyRelocated ? 2 : 0
  return {
    ...prev,
    hideoutMoveQuest: { status: 'active', phaseIndex },
  }
}

/**
 * Pure reducer for hideout move quest events.
 * Phase 0: complete a trip to Abandoned Farm.
 * Phase 1: use the move hideout button.
 * Phase 2: upgrade base station to level 2.
 */
export function reduceHideoutMoveQuest(
  prev: QuestRuntimeState,
  event: QuestProgressEvent,
): QuestRuntimeState | null {
  const hq = prev.hideoutMoveQuest
  if (hq.status !== 'active') return null

  const idx = hq.phaseIndex

  switch (idx) {
    case 0:
      if (event.type === 'expedition_returned_from' && event.locationId === 'abandoned-farm') {
        return { ...prev, hideoutMoveQuest: { status: 'active', phaseIndex: 1 } }
      }
      break
    case 1:
      if (event.type === 'hideout_relocated') {
        return { ...prev, hideoutMoveQuest: { status: 'active', phaseIndex: 2 } }
      }
      break
    case 2:
      if (
        event.type === 'workstation_upgraded' &&
        event.stationId === 'base_station' &&
        event.newLevel === 2
      ) {
        return {
          ...prev,
          hideoutMoveQuest: {
            status: 'completed',
            completedAt: Date.now(),
            acknowledged: false,
          },
        }
      }
      break
    default:
      break
  }

  return null
}

/** Whether the "Move hideout" button should be visible on the base station screen */
export function isHideoutMoveButtonVisible(q: QuestRuntimeState): boolean {
  const hq = q.hideoutMoveQuest
  if (hq.status === 'active') return hq.phaseIndex >= 1
  if (hq.status === 'completed') return true
  return false
}

/** Trips tab: A2 after Utrecht gateway is offered or underway */
export function isA2RegionVisibleInTrips(q: QuestRuntimeState): boolean {
  const s = q.utrechtGateway.status
  return s === 'available' || s === 'active' || s === 'completed'
}

/** Trips tab: Utrecht city after gateway completes */
export function isUtrechtRegionVisibleInTrips(q: QuestRuntimeState): boolean {
  return q.utrechtGateway.status === 'completed'
}

/** inactive → available when Den Bosch gateway story finished and all four city sites hit 100% */
export function reduceUtrechtGatewayAvailability(
  prev: QuestRuntimeState,
  explorationState: Partial<Record<LocationId, LocationExplorationState>> | undefined,
): QuestRuntimeState | null {
  if (prev.utrechtGateway.status !== 'inactive') return null
  if (prev.denBoschGateway.status !== 'completed') return null
  if (!isDenBoschFullyExplored(explorationState)) return null
  return { ...prev, utrechtGateway: { status: 'available' } }
}

/** available → active */
export function reduceUtrechtGatewayStart(
  prev: QuestRuntimeState,
  survivorsAtStart: number,
): QuestRuntimeState | null {
  if (prev.utrechtGateway.status !== 'available') return null
  return {
    ...prev,
    utrechtGateway: {
      status: 'active',
      startedAt: Date.now(),
      phaseIndex: 0,
      survivorsAtStart,
      nonTraderRecruitsSinceStart: 0,
      scrapLootValuePostBroadcast: 0,
      electronicsLootValuePostBroadcast: 0,
    },
  }
}

/** While active and broadcast exists: accumulate scrap-category loot (electronics counted separately). */
export function reduceUtrechtGatewayExpeditionLoot(
  prev: QuestRuntimeState,
  collectedItems: ItemStack[],
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
): QuestRuntimeState | null {
  const g = prev.utrechtGateway
  if (g.status !== 'active') return null
  if (!workstations.broadcast_station?.built) return null

  let scrap = g.scrapLootValuePostBroadcast
  let elec = g.electronicsLootValuePostBroadcast

  for (const stack of collectedItems) {
    const def = getItemDef(stack.defId)
    if (!def || def.category !== 'scrap') continue
    const add = def.value * stack.quantity
    if (stack.defId === 'scrap-electronics') {
      elec = Math.min(
        UTRECHT_GATEWAY_TARGETS.electronicsLootValuePostBroadcast,
        elec + add,
      )
    } else {
      scrap = Math.min(
        UTRECHT_GATEWAY_TARGETS.scrapLootValuePostBroadcast,
        scrap + add,
      )
    }
  }

  if (
    scrap === g.scrapLootValuePostBroadcast &&
    elec === g.electronicsLootValuePostBroadcast
  ) {
    return null
  }

  const next: QuestRuntimeState = {
    ...prev,
    utrechtGateway: {
      ...g,
      scrapLootValuePostBroadcast: scrap,
      electronicsLootValuePostBroadcast: elec,
    },
  }
  return tryCompleteUtrechtGateway(next, workstations)
}

export function incrementUtrechtGatewayRecruit(
  prev: QuestRuntimeState,
  role: SurvivorRole,
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
): QuestRuntimeState | null {
  const g = prev.utrechtGateway
  if (g.status !== 'active') return null
  if (!workstations.broadcast_station?.built) return null
  if (role === 'Trader') return null
  const nextCount = Math.min(
    UTRECHT_GATEWAY_TARGETS.nonTraderRecruits,
    g.nonTraderRecruitsSinceStart + 1,
  )
  if (nextCount === g.nonTraderRecruitsSinceStart) return null
  const next: QuestRuntimeState = {
    ...prev,
    utrechtGateway: { ...g, nonTraderRecruitsSinceStart: nextCount },
  }
  return tryCompleteUtrechtGateway(next, workstations)
}

export function tryCompleteUtrechtGateway(
  prev: QuestRuntimeState,
  workstations: Record<WorkstationId, WorkstationRuntimeState>,
): QuestRuntimeState | null {
  const g = prev.utrechtGateway
  if (g.status !== 'active') return null
  if (!workstations.broadcast_station?.built) return null
  if (g.nonTraderRecruitsSinceStart < UTRECHT_GATEWAY_TARGETS.nonTraderRecruits)
    return null
  if (
    g.scrapLootValuePostBroadcast <
    UTRECHT_GATEWAY_TARGETS.scrapLootValuePostBroadcast
  )
    return null
  if (
    g.electronicsLootValuePostBroadcast <
    UTRECHT_GATEWAY_TARGETS.electronicsLootValuePostBroadcast
  )
    return null
  return {
    ...prev,
    utrechtGateway: {
      status: 'completed',
      completedAt: Date.now(),
      acknowledged: false,
    },
  }
}

/** Migrate legacy onboarding steps into quest + done onboarding */
export function migrateOnboardingToQuests(
  step: string,
  kitchenBuilt: boolean,
): { onboardingStep: 'done'; quests: QuestRuntimeState } | null {
  if (
    step !== 'first_expedition' &&
    step !== 'first_return' &&
    step !== 'workstation_hint'
  ) {
    return null
  }

  let phaseIndex = 0
  if (step === 'first_return') phaseIndex = 1
  if (step === 'workstation_hint') {
    phaseIndex = kitchenBuilt ? 3 : 2
  }

  return {
    onboardingStep: 'done',
    quests: mergeQuestRuntimeState({
      starterSurvival: { status: 'active', phaseIndex },
    }),
  }
}
